import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components/macro';

import InfiniteScroll from 'react-infinite-scroller';

import JobCardRenderer from './JobCardRenderer';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import NoJobsIllustration from './const/assets/NoJobsAvailable.svg';

import colors from '../../assets/colors';
import fontSize from '../../assets/fontSize';

import { fetchJobListJobs } from '../../services/axios';
import { generateJobSearchParams } from '../../services/helpers';

const LoaderContainer = styled.div`
  min-height: 500px;
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const InfiniteLoaderWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;
const Loader = styled(CircularProgress)`
  && {
    svg {
      color: ${colors.primary};
    }
  }
`;
const JobCardWrapper = styled.div`
  margin: 1rem 0;
`;
const NoJobsAvailableWrapper = styled.div`
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const NoJobsText = styled.p`
  font-size: ${fontSize.large};
`;
const NoJobsImage = styled.img`
  width: 150px;
`;
const ResetFiltersButton = styled(Button)`
  && {
    text-transform: none;
    color: ${colors.primary};
  }
`;

const NO_JOBS_AVAILABLE = 0;

const JobsList = props => {
  const { activeFilters, resetFilters, hasPerformedInitialLoad, isMobile } =
    props;
  const [hasLoadedJobs, setHasLoadedJobs] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    const loadJobListJobs = async () => {
      setHasLoadedJobs(false);
      setNextCursor(null);

      const params = generateJobSearchParams(activeFilters);
      const response = await fetchJobListJobs(params);

      if (response?.isSuccess) {
        setJobs(response?.jobs);
        setHasLoadedJobs(true);
        setNextCursor(response?.nextCursor);
      }
    };
    if (hasPerformedInitialLoad) {
      loadJobListJobs();
    }
  }, [activeFilters, hasPerformedInitialLoad]);

  const loadMore = useCallback(async () => {
    try {
      const params = generateJobSearchParams(activeFilters, nextCursor);
      const response = await fetchJobListJobs(params);

      setNextCursor(null);

      if (response?.isSuccess) {
        setJobs(prev => [...prev, ...response?.jobs]);
      }

      if (response?.nextCursor) {
        setNextCursor(response?.nextCursor);
      } else {
        setNextCursor(null);
      }
    } finally {
      setHasLoadedJobs(true);
    }
  }, [activeFilters, nextCursor]);

  if (!hasLoadedJobs) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  if (jobs?.length === NO_JOBS_AVAILABLE) {
    return (
      <NoJobsAvailableWrapper>
        <NoJobsImage src={NoJobsIllustration} alt="No jobs available" />
        <NoJobsText>No jobs available</NoJobsText>
        <ResetFiltersButton onClick={resetFilters}>
          Reset filters
        </ResetFiltersButton>
      </NoJobsAvailableWrapper>
    );
  }

  return (
    <InfiniteScroll
      pageStart={0}
      loadMore={loadMore}
      hasMore={Boolean(nextCursor)}
      loader={
        <InfiniteLoaderWrap>
          <Loader />
        </InfiniteLoaderWrap>
      }
      useWindow={isMobile ? true : false}
    >
      {jobs?.map(job => {
        return (
          <JobCardWrapper key={`${job?.posted_at}`}>
            <JobCardRenderer job={job} />
          </JobCardWrapper>
        );
      })}
    </InfiniteScroll>
  );
};

export default JobsList;
